import { ReactComponent as CollibraIcon } from 'new/components/icons/collibra.svg';
import { ReactComponent as AlationIcon } from 'new/components/icons/alation.svg';
import { ReactComponent as DataWorldIcon } from 'new/components/icons/data.world.svg';
import { ReactComponent as OpenAiIcon } from 'new/components/icons/openai.svg';
import { ReactComponent as AzureOpenAiIcon } from 'new/components/icons/azure-openai.svg';
import { ReactComponent as GoogleAiIcon } from 'new/components/icons/google-ai.svg';
import React from 'react';

type CredentialConfig = {
  [key: string]: string | boolean;
};

type CredentialField = {
  name: string;
  display_name: string;
  description?: string;
  type: string;
  default?: string | boolean;
  required: boolean;
  inputType?: string;
  isHiddenField?: boolean;
  values?: string[];
  options?: {
    label: string;
    value: string;
    description?: string;
    isDisabled?: boolean;
  }[];
  dependsOn?: any;
};

type Meta = {
  name: string;
  type: string;
  displayName: string;
  IconComponent: React.FunctionComponent<React.SVGAttributes<SVGElement>>;
  description: string;
  longDescription: string;
};

type Credential = {
  type: string;
  config: CredentialConfig;
  fields: CredentialField[];
};

type Field = {
  name: string;
  display_name: string;
  description?: string;
  type: string;
  inputType?: string;
  required: boolean;
  isHiddenField?: boolean;
  default?: string | boolean;
  options?: { label: string; value: string; description?: string }[];
  values?: string[];
};

export type Integration = {
  meta: Meta;
  credential: Credential;
  fields: Field[];
};

type Integrations = {
  [name: string]: Integration;
};

const INTEGRATION_TYPE_DATA_CATALOG = 'INTEGRATION_TYPE_DATA_CATALOG';
const INTEGRATION_TYPE_GEN_AI = 'INTEGRATION_TYPE_GEN_AI';

export const integrationTypes = {
  data_catalog: INTEGRATION_TYPE_DATA_CATALOG,
  gen_ai: INTEGRATION_TYPE_GEN_AI,
} as const;

export const EXTERNAL_INTEGRATIONS: Integrations = {
  genai_openai: {
    meta: {
      name: 'genai_openai',
      type: integrationTypes.gen_ai,
      displayName: 'OpenAI',
      IconComponent: OpenAiIcon,
      description:
        "Link your OpenAI account with Nexla. Empower your organization's members for getting recommendations from Gen AI.",
      longDescription:
        'Link your OpenAI account with Nexla. Once you connect and activate this integration you will be able to get recommendations.',
    },
    credential: {
      type: 'genai_openai',
      config: {
        'auth.type': 'API_KEY',
        'ignore.ssl.cert.validation': false,
        'skip.validation': '${skip.validation}',
        'test.method': 'POST',
        'test.content.type': 'application/json',
        'test.body':
          '{"model": "${llm_provider_model_version}","messages": [{"role": "system","content": "You are a helpful assistant."},{"role": "user","content": "Write a random word?"}]}',
        'test.url': 'https://api.openai.com/${llm_provider_api_version}/chat/completions',
        'api.key.include.mode': 'HEADER',
        'api.key.auth.key': 'Authorization',
        'api.key.auth.value': 'Bearer ${api_key_header_value}',
        llm_provider_api_key: 'Bearer ${api_key_header_value}',
        llm_provider_private_url: '${llm_provider_private_url}',
        llm_provider_model_version: '${llm_provider_model_version}',
        llm_provider_api_version: '${llm_provider_api_version}',
      },
      fields: [
        {
          name: 'llm_provider_private_url',
          display_name: 'API Base URL',
          description:
            'Enter the **URL of the OpenAI installation**.\n\nThis is typically in the format \u0060https://api.openai.com\u0060. ',
          type: 'string',
          default: 'https://api.openai.com',
          required: true,
        },
        {
          name: 'llm_provider_api_version',
          display_name: 'API Version',
          description: 'Select the **API version** to be used.',
          type: 'string',
          default: 'v1',
          required: true,
        },
        {
          name: 'api_key_header_value',
          display_name: 'API Key',
          description: 'Enter your OpenAI API key. This can be found in the OpenAI Console.',
          type: 'string',
          required: true,
          inputType: 'password',
        },
        {
          name: 'llm_provider_model_version',
          display_name: 'OpenAI Model',
          description:
            'Select the **OpenAI model** to be used. You can also type a model name if you do not see it in the list.',
          type: 'string',
          inputType: 'select-creatable',
          default: 'gpt-5.2',
          values: ['gpt-5.2', 'gpt-5.2-pro', 'gpt-5.1', 'gpt-5-mini'],
          required: true,
        },
        {
          name: 'skip.validation',
          display_name: 'Skip Credential Validation',
          inputType: 'checkbox',
          description:
            'Skip the credential validation while testing the connection.\n\nIf this is set to \u0060true\u0060, the **Test URL** and **Test Body** fields will be ignored.',
          type: 'boolean',
          required: true,
          default: false,
        },
      ],
    },
    fields: [
      {
        name: 'openai_model',
        display_name: 'OpenAI Model',
        description:
          'Select the **OpenAI model** to be used. You can also type a model name if you do not see it in the list.',
        type: 'string',
        inputType: 'select-creatable',
        default: 'gpt-5.2',
        values: ['gpt-5.2', 'gpt-5.2-pro', 'gpt-5.1', 'gpt-5-mini'],
        required: true,
      },
      {
        name: 'enable_doc_recommendations',
        display_name: 'Enable Doc Recommendations',
        description: '',
        type: 'boolean',
        default: true,
        isHiddenField: true,
        required: true,
      },
    ],
  },
  genai_azure_openai: {
    meta: {
      name: 'genai_azure_openai',
      type: integrationTypes.gen_ai,
      displayName: 'Azure OpenAI',
      IconComponent: AzureOpenAiIcon,
      description:
        "Link your Azure OpenAI account with Nexla. Empower your organization's members for getting recommendations from Gen AI.",
      longDescription:
        'Link your Azure OpenAI account with Nexla. Once you connect and activate this integration you will be able to get recommendations.',
    },
    credential: {
      type: 'genai_azure_openai',
      config: {
        'auth.type': 'API_KEY',
        'ignore.ssl.cert.validation': false,
        'skip.validation': '${skip.validation}',
        'test.method': 'POST',
        'test.content.type': 'application/json',
        'test.body':
          '{"messages": [{"role": "system","content": "You are a helpful assistant."},{"role": "user","content": "Write a random word?"}]}',
        'test.url':
          '${azure_openai_endpoint}/openai/deployments/${azure_openai_model}/chat/completions?api-version=${azure_openai_api_version}',
        'api.key.include.mode': 'HEADER',
        'api.key.auth.key': '${api_key_header_name}',
        'api.key.auth.value': '${api_key_header_value}',
        llm_provider_api_key: '${api_key_header_value}',
        llm_provider_private_url: '${azure_openai_endpoint}',
        llm_provider_model_version: '${azure_openai_model}',
        llm_provider_api_version: '${azure_openai_api_version}',
        azure_openai_model: '${azure_openai_model}',
        azure_openai_endpoint: '${azure_openai_endpoint}',
        azure_openai_api_version: '${azure_openai_api_version}',
      },
      fields: [
        {
          name: 'azure_openai_endpoint',
          display_name: 'Azure endpoint',
          description:
            'Enter the **Azure OpenAI endpoint** (e.g., \u0060https://<resource>.openai.azure.com\u0060).',
          type: 'string',
          default: 'https://<resource>.openai.azure.com',
          required: true,
        },
        {
          name: 'api_key_header_name',
          display_name: 'Request Header Name',
          description:
            'Choose the header for authenticating requests. Azure OpenAI supports two authentication methods: \n\n• **api-key** — Use for key-based authentication. This sends your secret Azure OpenAI API key directly in the request header.\n\n• **Authorization** — Use for token-based authentication (**Bearer** tokens) via Microsoft Entra ID. Requires a token with the \u0060https://cognitiveservices.azure.com/.default\u0060 scope.',
          type: 'string',
          inputType: 'select-creatable',
          values: ['Authorization', 'api-key'],
          required: true,
          default: 'api-key',
        },
        {
          name: 'api_key_header_value',
          display_name: 'Request Header Value',
          description:
            '• If using **api-key**: enter your Azure OpenAI API key found in the Azure Portal.\n\n• If using **Authorization**: enter `Bearer {token}`. To generate a token via Azure CLI use: \u0060az account get-access-token --resource https://cognitiveservices.azure.com\u0060',
          type: 'string',
          default: '{your-azure-openai-api-key}',
          required: true,
          inputType: 'password',
        },
        {
          name: 'azure_openai_model',
          display_name: 'Deployment name / Model',
          description:
            'Enter the **deployment name** (used as the model). You can also type a deployment name if you do not see it in the list.',
          type: 'string',
          inputType: 'select-creatable',
          default: 'gpt-5.2',
          values: [
            'gpt-4o',
            'gpt-4o-mini',
            'gpt-4',
            'gpt-35-turbo',
            'o1',
            'gpt-5.2',
            'gpt-5.2-pro',
            'gpt-5.1',
            'gpt-5-mini',
          ],
          required: true,
        },
        {
          name: 'azure_openai_api_version',
          display_name: 'Azure API version',
          description: 'Enter the **Azure OpenAI API version** (e.g., \u00602025-12-11\u0060).',
          type: 'string',
          default: '2025-12-11',
          required: true,
        },
        {
          name: 'skip.validation',
          display_name: 'Skip Credential Validation',
          inputType: 'checkbox',
          description:
            'Skip the credential validation while testing the connection.\n\nIf this is set to \u0060true\u0060, the **Test URL** and **Test Body** fields will be ignored.',
          type: 'boolean',
          required: true,
          default: false,
        },
      ],
    },
    fields: [
      {
        name: 'azure_openai_model',
        display_name: 'Deployment name / Model',
        description:
          'Enter the **deployment name** (used as the model). You can also type a deployment name if you do not see it in the list.',
        type: 'string',
        inputType: 'select-creatable',
        default: 'gpt-5.2',
        values: [
          'gpt-4o',
          'gpt-4o-mini',
          'gpt-4',
          'gpt-35-turbo',
          'o1',
          'gpt-5.2',
          'gpt-5.2-pro',
          'gpt-5.1',
          'gpt-5-mini',
        ],
        required: true,
      },
      {
        name: 'azure_openai_api_version',
        display_name: 'Azure API version',
        description: 'Enter the **Azure OpenAI API version** (e.g., \u00602025-12-11\u0060).',
        type: 'string',
        default: '2025-12-11',
        required: true,
      },
      {
        name: 'enable_doc_recommendations',
        display_name: 'Enable Doc Recommendations',
        description: '',
        type: 'boolean',
        default: true,
        isHiddenField: true,
        required: true,
      },
    ],
  },
  genai_googleai: {
    meta: {
      name: 'genai_googleai',
      type: integrationTypes.gen_ai,
      displayName: 'Google AI',
      IconComponent: GoogleAiIcon,
      description:
        "Link your Google AI account with Nexla. Empower your organization's members for getting recommendations from Gen AI.",
      longDescription:
        'Link your Google AI account with Nexla. Once you connect and activate this integration you will be able to get recommendations.',
    },
    credential: {
      type: 'genai_googleai',
      config: {
        'auth.type': 'API_KEY',
        'ignore.ssl.cert.validation': false,
        'test.method': 'POST',
        'skip.validation': '${skip.validation}',
        'test.content.type': 'application/json',
        'test.url':
          'https://generativelanguage.googleapis.com/${llm_provider_api_version}/models/${llm_provider_model_version}:generateContent',
        'test.body': '{"contents": [{ "parts":[{ "text": "Write a random word."}]}]}',
        'api.key.include.mode': 'HEADER',
        'api.key.auth.key': 'x-goog-api-key',
        'api.key.auth.value': '${token}',
        llm_provider_api_key: '${token}',
        llm_provider_private_url: 'https://generativelanguage.googleapis.com',
        llm_provider_model_version: '${llm_provider_model_version}',
        llm_provider_api_version: '${llm_provider_api_version}',
      },
      fields: [
        {
          name: 'token',
          display_name: 'GoogleAI API Key',
          description: 'Enter the **GoogleAI API Key** that should be used.',
          type: 'string',
          required: true,
          inputType: 'password',
        },
        {
          name: 'llm_provider_api_version',
          display_name: 'API Version',
          description: 'Select the **GoogleAI API version** to be used.',
          type: 'string',
          inputType: 'select-creatable',
          default: 'v1beta',
          values: ['v1', 'v1beta'],
          required: true,
        },
        {
          name: 'llm_provider_model_version',
          display_name: 'Google AI Model',
          description:
            'Select the **GoogleAI Model** to be used. You can also type a model name if you do not see it in the list.',
          type: 'string',
          inputType: 'select-creatable',
          default: 'gemini-flash-latest',
          values: [
            'gemini-flash-latest',
            'gemini-pro-latest',
            'gemini-3.1-pro-preview',
            'gemini-3.1-flash-lite-preview',
            'gemini-3-flash-preview',
            'gemini-2.5-pro',
            'gemini-2.5-flash',
            'gemini-2.5-flash-lite',
            'deep-research-pro-preview',
            'gemma-3-27b-it',
            'gemma-3-12b-it',
            'gemma-3-4b-it',
            'gemma-3-1b-it',
          ],
          required: true,
        },
        {
          name: 'skip.validation',
          display_name: 'Skip Credential Validation',
          inputType: 'checkbox',
          description: 'Skip the credential validation while testing the connection.',
          type: 'boolean',
          required: true,
          default: false,
        },
      ],
    },
    fields: [
      {
        name: 'api_version',
        display_name: 'API Version',
        description: 'Select the **GoogleAI API version** to be used.',
        type: 'string',
        inputType: 'select-creatable',
        default: 'v1beta',
        values: ['v1', 'v1beta'],
        required: true,
      },
      {
        name: 'googleai_model',
        display_name: 'Google AI Model',
        description:
          'Select the **GoogleAI Model** to be used. You can also type a model name if you do not see it in the list.',
        type: 'string',
        inputType: 'select-creatable',
        default: 'gemini-flash-latest',
        values: [
          'gemini-flash-latest',
          'gemini-pro-latest',
          'gemini-3.1-pro-preview',
          'gemini-3.1-flash-lite-preview',
          'gemini-3-flash-preview',
          'gemini-2.5-pro',
          'gemini-2.5-flash',
          'gemini-2.5-flash-lite',
          'deep-research-pro-preview',
          'gemma-3-27b-it',
          'gemma-3-12b-it',
          'gemma-3-4b-it',
          'gemma-3-1b-it',
        ],
        required: true,
      },
      {
        name: 'enable_doc_recommendations',
        display_name: 'Enable Doc Recommendations',
        description: '',
        type: 'boolean',
        default: true,
        isHiddenField: true,
        required: true,
      },
    ],
  },
  collibra: {
    meta: {
      name: 'collibra',
      type: integrationTypes.data_catalog,
      displayName: 'Collibra',
      IconComponent: CollibraIcon,
      description:
        "Link your Collibra account with Nexla. Empower your organization's members for registering their Nexsets in  Collibra.",
      longDescription:
        "Link your Collibra account with Nexla. Once you connect and activate this integration all your organization's members will be able to register and update their Nexsets in Collibra's Data Catalog.",
    },
    credential: {
      type: 'collibra',
      config: {
        'auth.type': 'BASIC',
        'ignore.ssl.cert.validation': false,
        'test.method': 'GET',
        'test.content.type': 'application/json',
        'jwt.enabled': false,
        'hmac.enabled': false,
        'ui.cert_signed': false,
        'basic.username': '${basic.username}',
        'basic.password': '${basic.password}',
        'test.url': '${collibra.instance_url}/rest/2.0/users',
        'collibra.instance_url': '${collibra.instance_url}',
      },
      fields: [
        {
          name: 'collibra.instance_url',
          display_name: 'Collibra Base URL',
          description:
            'Enter the **URL of your Collibra installation**.\n\n**This is typically in the format \u0060https://nexla.collibra.com\u0060.**',
          type: 'string',
          default: 'https://nexla.collibra.com',
          required: true,
        },
        {
          name: 'basic.username',
          display_name: 'Username',
          description:
            'Enter the **Collibra username** that should be used for updating your Collibra account.',
          type: 'string',
          required: true,
        },
        {
          name: 'basic.password',
          display_name: 'Password',
          description:
            'Enter the **password for the Collibra user** that should be used for updating your Collibra account.',
          type: 'string',
          required: true,
          inputType: 'password',
        },
      ],
    },
    fields: [
      {
        name: 'schema_id',
        display_name: 'Collibra Schema ID',
        description:
          "Enter the **Collibra Schema ID** where you want your Nexla organization's Nexsets to be published.",
        type: 'string',
        required: true,
      },
    ],
  },
  'data.world': {
    meta: {
      name: 'data.world',
      type: integrationTypes.data_catalog,
      displayName: 'data.world',
      IconComponent: DataWorldIcon,
      description:
        "Link your data.world account with Nexla. Empower your organization's members for registering their Nexsets in data.world.",
      longDescription:
        "Link your data.world account with Nexla. Once you connect and activate this integration all your organization's members will be able to register and update their Nexsets in data.world's Data Catalog.",
    },
    credential: {
      type: 'data_world',
      config: {
        'auth.type': 'API_KEY',
        'ignore.ssl.cert.validation': false,
        'test.method': 'GET',
        'test.content.type': 'application/json',
        'jwt.enabled': false,
        'skip.validation': false,
        'hmac.enabled': false,
        'ui.cert_signed': false,
        'data_world.api_url': 'https://api.data.world/v0',
        'test.url': 'https://api.data.world/v0/user',
        'data_world.instance_url': '${data_world.instance_url}',
        'api.key.include.mode': 'HEADER',
        'api.key.auth.key': 'Authorization',
        'api.key.auth.value': 'Bearer ${token}',
      },
      fields: [
        {
          name: 'data_world.instance_url',
          display_name: 'Your data.world Base URL',
          description:
            'Enter the **URL of your data.world installation**.\n\n**This is typically in the format \u0060https://nexlapartner.app.data.world\u0060.**',
          type: 'string',
          default: 'https://nexlapartner.app.data.world',
          required: true,
        },
        {
          name: 'token',
          display_name: 'Service Account Token',
          description:
            'Enter the **data.world Service Account Token** that should be used for updating your data.world account.',
          type: 'string',
          required: true,
          inputType: 'password',
        },
      ],
    },
    fields: [
      {
        name: 'schema_id',
        display_name: 'Schema ID',
        type: 'string',
        required: true,
        isHiddenField: true,
        default: 'None',
      },
      {
        name: 'owner',
        display_name: 'data.world Organization',
        description:
          "Enter the **data.world organization** where you want your Nexla organization's Nexsets to be published.",
        type: 'string',
        required: true,
      },
      {
        name: 'collection',
        display_name: 'Collection Name',
        description:
          'Enter the **data.world collection name** you wish to use for organizing your Nexsets.\n\n**We recommend setting this to \u0060Nexla Collection\u0060.**',
        type: 'string',
        required: true,
      },
      {
        name: 'type_label',
        display_name: 'Type Label',
        description:
          'Enter the **label of the data.world resource** you have created in data.world organiation for listing your Nexsets in data.world.\n\n**We recommend setting this to \u0060Nexla Nexset\u0060.**',
        type: 'string',
        required: true,
      },
    ],
  },
  alation: {
    meta: {
      name: 'alation',
      type: integrationTypes.data_catalog,
      displayName: 'Alation',
      IconComponent: AlationIcon,
      description:
        "Link your Alation account with Nexla. Empower your organization's members for registering their Nexsets in Alation.",
      longDescription:
        "Link your Alation account with Nexla. Once you connect and activate this integration all your organization's members will be able to register and update their Nexsets in Alation's Data Catalog.",
    },
    credential: {
      type: 'alation',
      config: {
        'auth.type': '${auth.type}',
        'ignore.ssl.cert.validation': false,
        'test.method': 'GET',
        'test.content.type': 'application/json',
        'jwt.enabled': false,
        'skip.validation': true,
        'hmac.enabled': false,
        'ui.cert_signed': false,
        'test.url': '${alation.instance_url}',
        'alation.instance_url': '${alation.instance_url}',
        'basic.username': '${basic.username}',
        'basic.password': '${basic.password}',
      },
      fields: [
        {
          name: 'auth.type',
          display_name: 'Alation Authentication Type',
          description:
            'Select the **authentication type** to be used for connecting with your alation account.\n See [Alation Service Account](https://developer.alation.com/dev/docs/creating-an-api-service-account) for more information.',
          type: 'string',
          inputType: 'select',
          required: true,
          default: 'TOKEN',
          options: [
            {
              label: 'Service Account Authentication',
              description:
                'Use an Alation service account for authentication.\n.This mechanism will work for both Alation Cloud and Alation On-Premise installations. ',
              value: 'TOKEN',
            },
            {
              label: 'OAuth Authentication',
              description:
                'Use a 2-legged OAuth flow for authentication. This mechanism will only work for Alation Cloud instances and requires additional setup in Alation.',
              value: 'OAUTH',
              isDisabled: true,
            },
          ],
        },
        {
          name: 'alation.instance_url',
          display_name: 'Alation Instance URL',
          description:
            'Enter the **URL of your Alation installation**.\n\n**This is typically in the format \u0060https://alation.nexla.com\u0060.**',
          type: 'string',
          default: 'https://alation.nexla.com',
          required: true,
        },
        {
          name: 'basic.username',
          display_name: 'Username',
          type: 'string',
          required: true,
          description:
            'Enter the username for your Alation Service Account. Nexla will use this to authenticate with Alation.',
        },
        {
          name: 'basic.password',
          display_name: 'Password',
          type: 'string',
          inputType: 'password',
          required: true,
          description:
            'Enter the password for your Alation Service Account. Nexla will use this to authenticate with Alation.',
        },
      ],
    },
    fields: [
      {
        name: 'schema_id',
        display_name: 'Schema ID',
        description:
          "Enter the **Alation Schema ID** where you want your Nexla organization's Nexsets to be published.",
        type: 'integer',
        required: true,
        isHiddenField: false,
      },
      {
        name: 'include_samples',
        display_name: 'Include Nexset Samples?',
        description:
          "By disabling this option, you can prevent Nexla from sending Nexset samples to Alation. This can be useful if you're working with sensitive data.",
        type: 'string',
        inputType: 'select',
        required: true,
        default: 'true',
        options: [
          {
            label: 'Yes',
            value: 'true',
          },
          {
            label: 'No',
            value: 'false',
          },
        ],
      },
    ],
  },
};
//Being more explicit about item orders. Make sure the values in this list are in sync with the keys in EXTERNAL_INTEGRATIONS

export const EXTERNAL_INTEGRATIONS_ITEMS = {
  [integrationTypes.gen_ai]: ['genai_openai', 'genai_azure_openai', 'genai_googleai'],
  [integrationTypes.data_catalog]: ['alation', 'collibra', 'data.world'],
};
